/*import React, { useState, useEffect } from "react";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";

function Login() {
  const navigate = useNavigate();

  // step: 'password' | 'device' | 'biometric' | 'totp'
  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [deviceCode, setDeviceCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [hasAnyBiometricCredential, setHasAnyBiometricCredential] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [biometricStatus, setBiometricStatus] = useState("idle"); // idle | prompting | failed

  useEffect(() => {
    checkLogin();
  }, []);

  const checkLogin = async () => {
    try {
      const res = await api.get("/profile");
      if (res.data.success) {
        navigate("/dashboard");
      }
    } catch (error) {
      // Not logged in yet — stay on login screen
    }
  };

  const decideNextStepAfterDeviceOrPassword = (biometricAvailable) => {
    if (biometricAvailable) {
      setStep("biometric");
    } else {
      setStep("totp");
    }
  };

  // ===== STEP 1: PASSWORD =====
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/login", { email, password });

      if (res.data.success) {
        setHasAnyBiometricCredential(res.data.hasAnyBiometricCredential);

        if (res.data.requiresDeviceCheck) {
          // Auto-send the email code right away
          await api.post("/mfa/device/send-code");
          setStep("device");
        } else {
          decideNextStepAfterDeviceOrPassword(res.data.hasAnyBiometricCredential);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // ===== STEP 2: DEVICE CHECK =====
  const handleDeviceVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const deviceName = window.navigator.userAgent.includes("Windows")
        ? "Windows device"
        : window.navigator.userAgent.includes("Mac")
        ? "Mac device"
        : "This device";

      const res = await api.post("/mfa/device/verify-code", {
        code: deviceCode,
        deviceName,
      });

      if (res.data.success) {
        decideNextStepAfterDeviceOrPassword(hasAnyBiometricCredential);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendDeviceCode = async () => {
    setError("");
    try {
      await api.post("/mfa/device/send-code");
      setError("A new code has been sent to your email.");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend code");
    }
  };

  // ===== STEP 3: BIOMETRIC =====
  const handleBiometricLogin = async () => {
    setError("");
    setBiometricStatus("prompting");
    try {
      const optionsRes = await api.post("/webauthn/login/options");
      const options = optionsRes.data.options;

      const authResponse = await startAuthentication({ optionsJSON: options });

      await api.post("/webauthn/login/verify", authResponse);

      setStep("totp");
    } catch (err) {
      // Most likely: this device doesn't have a matching passkey, or user cancelled
      setBiometricStatus("failed");
      setError(
        "Biometric not available on this device. You can continue with your authenticator app instead."
      );
    }
  };

  const skipBiometric = () => {
    setStep("totp");
    setError("");
  };

  // ===== STEP 4: TOTP =====
  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/mfa/totp/verify-login", { code: totpCode });
      if (res.data.success) {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Admin Login</h2>
          <p style={styles.subtitle}>{stepSubtitle(step)}</p>
        </div>

        {error && <p style={styles.errorText}>{error}</p>}

       
        {step === "password" && (
          <form onSubmit={handlePasswordSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                placeholder="name@company.com"
                style={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                placeholder="••••••••"
                style={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
              disabled={loading}
            >
              {loading ? "Checking..." : "Continue"}
            </button>
          </form>
        )}

        
        {step === "device" && (
          <form onSubmit={handleDeviceVerify} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Enter the 6-digit code sent to your email</label>
              <input
                type="text"
                placeholder="123456"
                style={styles.input}
                value={deviceCode}
                onChange={(e) => setDeviceCode(e.target.value)}
                maxLength={6}
                required
              />
            </div>

            <button
              type="submit"
              style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
              disabled={loading}
            >
              {loading ? "Verifying..." : "Verify Device"}
            </button>

            <button type="button" onClick={handleResendDeviceCode} style={styles.linkButton}>
              Resend code
            </button>
          </form>
        )}

       
        {step === "biometric" && (
          <div style={styles.form}>
            <p style={styles.centerText}>
              Use your fingerprint or Face ID to continue.
            </p>

            <button
              type="button"
              style={styles.button}
              onClick={handleBiometricLogin}
              disabled={biometricStatus === "prompting"}
            >
              {biometricStatus === "prompting" ? "Waiting for scan..." : "Scan Fingerprint / Face ID"}
            </button>

            {biometricStatus === "failed" && (
              <button type="button" onClick={skipBiometric} style={styles.linkButton}>
                Continue with Authenticator App instead
              </button>
            )}
          </div>
        )}

       
        {step === "totp" && (
          <form onSubmit={handleTotpSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Enter your authenticator app code</label>
              <input
                type="text"
                placeholder="123456"
                style={styles.input}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                maxLength={6}
                required
              />
            </div>

            <button
              type="submit"
              style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
              disabled={loading}
            >
              {loading ? "Verifying..." : "Sign In"}
            </button>
          </form>
        )}

        <footer style={styles.footer}>
          &copy; {new Date().getFullYear()} Internal Admin System
        </footer>
      </div>
    </div>
  );
}

const stepSubtitle = (step) => {
  switch (step) {
    case "password":
      return "Enter your details to access the dashboard";
    case "device":
      return "New device detected — verify it's you";
    case "biometric":
      return "Confirm your identity";
    case "totp":
      return "Final step — enter your authenticator code";
    default:
      return "";
  }
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f0f2f5",
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  card: {
    backgroundColor: "#ffffff",
    padding: "40px",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    width: "100%",
    maxWidth: "400px",
  },
  header: {
    textAlign: "center",
    marginBottom: "24px",
  },
  title: {
    margin: "0",
    fontSize: "24px",
    fontWeight: "700",
    color: "#1a202c",
  },
  subtitle: {
    margin: "8px 0 0",
    fontSize: "14px",
    color: "#718096",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#4a5568",
  },
  input: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "16px",
    outline: "none",
    transition: "border-color 0.2s",
  },
  button: {
    marginTop: "10px",
    padding: "12px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  buttonDisabled: {
    backgroundColor: "#94a3b8",
    cursor: "not-allowed",
  },
  linkButton: {
    background: "none",
    border: "none",
    color: "#2563eb",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    textAlign: "center",
    marginTop: "-8px",
  },
  centerText: {
    fontSize: "14px",
    color: "#4a5568",
    textAlign: "center",
  },
  errorText: {
    color: "#b91c1c",
    fontSize: "13px",
    fontWeight: "600",
    textAlign: "center",
    marginBottom: "16px",
  },
  footer: {
    marginTop: "32px",
    textAlign: "center",
    fontSize: "12px",
    color: "#a0aec0",
  },
};

export default Login;*/














import React, { useState, useEffect, useRef } from "react";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";

// 3D Engine Imports
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, MeshWobbleMaterial, PerspectiveCamera } from "@react-three/drei";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShieldCheck, 
  KeyRound, 
  Fingerprint, 
  Smartphone, 
  Terminal, 
  Lock, 
  Cpu, 
  CheckCircle2, 
  AlertTriangle,
  RefreshCw
} from "lucide-react";

// ==========================================
// 3D CYBERNETIC CORE (Interactive canvas element)
// ==========================================
function CyberCore({ step, loading }) {
  const meshRef = useRef();
  const wireframeRef = useRef();

  // Color map based on authentication step
  const getStepColor = () => {
    if (loading) return "#f59e0b"; // Amber on computing
    switch (step) {
      case "password": return "#3b82f6"; // Cyber Blue
      case "device": return "#ec4899";   // Neon Pink
      case "biometric": return "#10b981"; // Emerald Green
      case "totp": return "#8b5cf6";      // Electric Purple
      default: return "#3b82f6";
    }
  };

  const currentColor = getStepColor();

  useFrame((state, delta) => {
    // Accelerate rotation when loading
    const speedMultiplier = loading ? 4 : 1;
    if (meshRef.current) {
      meshRef.current.rotation.x += delta * 0.5 * speedMultiplier;
      meshRef.current.rotation.y += delta * 0.8 * speedMultiplier;
    }
    if (wireframeRef.current) {
      wireframeRef.current.rotation.x -= delta * 0.3 * speedMultiplier;
      wireframeRef.current.rotation.y -= delta * 0.4 * speedMultiplier;
    }
  });

  return (
    <Float speed={2} rotationIntensity={1} floatIntensity={2}>
      {/* Outer Wireframe Cage */}
      <mesh ref={wireframeRef}>
        <icosahedronGeometry args={[2.2, 1]} />
        <meshBasicMaterial color={currentColor} wireframe transparent opacity={0.25} />
      </mesh>

      {/* Inner Glowing Core */}
      <mesh ref={meshRef}>
        <octahedronGeometry args={[1.4, 0]} />
        <MeshWobbleMaterial 
          color={currentColor} 
          factor={loading ? 1.2 : 0.4} 
          speed={loading ? 5 : 2} 
          roughness={0.1}
          metalness={0.9}
        />
      </mesh>
    </Float>
  );
}

// ==========================================
// MAIN LOGIN COMPONENT
// ==========================================
export default function Login() {
  const navigate = useNavigate();

  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [deviceCode, setDeviceCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [hasAnyBiometricCredential, setHasAnyBiometricCredential] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [biometricStatus, setBiometricStatus] = useState("idle");

  useEffect(() => {
    checkLogin();
  }, []);

  const checkLogin = async () => {
    try {
      const res = await api.get("/profile");
      if (res.data.success) {
        navigate("/dashboard");
      }
    } catch (error) {
      // Not logged in yet
    }
  };

  const decideNextStepAfterDeviceOrPassword = (biometricAvailable) => {
    if (biometricAvailable) {
      setStep("biometric");
    } else {
      setStep("totp");
    }
  };

  // Step 1: Password Submit
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/login", { email, password });
      if (res.data.success) {
        setHasAnyBiometricCredential(res.data.hasAnyBiometricCredential);
        if (res.data.requiresDeviceCheck) {
          await api.post("/mfa/device/send-code");
          setStep("device");
        } else {
          decideNextStepAfterDeviceOrPassword(res.data.hasAnyBiometricCredential);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || "AUTHENTICATION REJECTED: Invalid Credentials");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Device Check
  const handleDeviceVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const deviceName = window.navigator.userAgent.includes("Windows")
        ? "Windows device"
        : window.navigator.userAgent.includes("Mac")
        ? "Mac device"
        : "This device";

      const res = await api.post("/mfa/device/verify-code", {
        code: deviceCode,
        deviceName,
      });

      if (res.data.success) {
        decideNextStepAfterDeviceOrPassword(hasAnyBiometricCredential);
      }
    } catch (err) {
      setError(err.response?.data?.message || "HANDSHAKE FAILED: Invalid Token Signature");
    } finally {
      setLoading(false);
    }
  };

  const handleResendDeviceCode = async () => {
    setError("");
    try {
      await api.post("/mfa/device/send-code");
      setError("PAYLOAD DISPATCHED: New verification key sent to inbox.");
    } catch (err) {
      setError(err.response?.data?.message || "Dispatch failure: Host unreachable");
    }
  };

  // Step 3: Biometric
  const handleBiometricLogin = async () => {
    setError("");
    setBiometricStatus("prompting");
    try {
      const optionsRes = await api.post("/webauthn/login/options");
      const options = optionsRes.data.options;
      const authResponse = await startAuthentication({ optionsJSON: options });
      await api.post("/webauthn/login/verify", authResponse);
      setStep("totp");
    } catch (err) {
      setBiometricStatus("failed");
      setError("BIOMETRIC REJECTED: Hardware scanner bypass requested.");
    }
  };

  const skipBiometric = () => {
    setStep("totp");
    setError("");
  };

  // Step 4: TOTP
  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/mfa/totp/verify-login", { code: totpCode });
      if (res.data.success) {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || "ACCESS DENIED: Cipher mismatch");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.viewport}>
      {/* CSS Keyframes & Global Animations */}
      <style>{`
        @keyframes scan {
          0% { top: 0%; opacity: 0; }
          50% { opacity: 0.8; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes gridPulse {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.35; }
        }
        .hud-input {
          background: rgba(15, 23, 42, 0.7) !important;
          border: 1px solid rgba(59, 130, 246, 0.3) !important;
          color: #00f0ff !important;
          font-family: 'Courier New', monospace !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .hud-input:focus {
          border-color: #00f0ff !important;
          box-shadow: 0 0 20px rgba(0, 240, 255, 0.4), inset 0 0 10px rgba(0, 240, 255, 0.2) !important;
          outline: none !important;
        }
        .hud-button {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.8), rgba(139, 92, 246, 0.8)) !important;
          border: 1px solid rgba(0, 240, 255, 0.6) !important;
          box-shadow: 0 0 15px rgba(59, 130, 246, 0.4) !important;
          transition: all 0.2s ease !important;
        }
        .hud-button:hover:not(:disabled) {
          transform: scale(1.02) !important;
          box-shadow: 0 0 30px rgba(0, 240, 255, 0.8) !important;
          background: linear-gradient(135deg, rgba(0, 240, 255, 0.9), rgba(139, 92, 246, 0.9)) !important;
        }
      `}</style>

      {/* BACKGROUND 3D CANVAS */}
      <div style={styles.canvasContainer}>
        <Canvas>
          <PerspectiveCamera makeDefault position={[0, 0, 6]} />
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} intensity={1.5} color="#00f0ff" />
          <pointLight position={[-10, -10, -10]} intensity={1.5} color="#ec4899" />
          <CyberCore step={step} loading={loading} />
        </Canvas>
      </div>

      {/* CYBER GRID & RETICLE OVERLAYS */}
      <div style={styles.gridOverlay} />
      <div style={styles.cornerTL} />
      <div style={styles.cornerTR} />
      <div style={styles.cornerBL} />
      <div style={styles.cornerBR} />

      {/* GAMIFIED HUD CARD */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={styles.hudCard}
      >
        {/* Dynamic Scanning Laser Bar */}
        <div style={styles.scannerLine} />

        {/* HUD HEADER */}
        <div style={styles.header}>
          <div style={styles.systemStatus}>
            <Cpu size={14} color="#00f0ff" />
            <span style={styles.systemStatusText}>SYS.LOC // SECURE_NODE_v4</span>
          </div>

          <h1 style={styles.title}>GADIMART GATEWAY</h1>

          {/* Gamified Step Tracker */}
          <div style={styles.trackerContainer}>
            <StepIndicator label="PASS" active={step === "password"} done={step !== "password"} />
            <div style={styles.trackerLine} />
            <StepIndicator label="DEV" active={step === "device"} done={["biometric", "totp"].includes(step)} />
            <div style={styles.trackerLine} />
            <StepIndicator label="BIO" active={step === "biometric"} done={step === "totp"} />
            <div style={styles.trackerLine} />
            <StepIndicator label="TOTP" active={step === "totp"} done={false} />
          </div>
        </div>

        {/* ERROR DISCLOSURE */}
        <AnimatePresence mode="wait">
          {error && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }} 
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              style={styles.errorBox}
            >
              <AlertTriangle size={16} color="#ef4444" />
              <span style={styles.errorText}>{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* STEP 1: PASSWORD FORM */}
        {step === "password" && (
          <form onSubmit={handlePasswordSubmit} style={styles.form}>
            <div style={styles.inputWrapper}>
              <label style={styles.label}>[IDENTITY_HASH]</label>
              <div style={styles.inputInner}>
                <Terminal size={18} color="#00f0ff" style={styles.inputIcon} />
                <input
                  type="email"
                  className="hud-input"
                  style={styles.input}
                  placeholder="admin@gadimart.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div style={styles.inputWrapper}>
              <label style={styles.label}>[MASTER_CIPHER]</label>
              <div style={styles.inputInner}>
                <Lock size={18} color="#00f0ff" style={styles.inputIcon} />
                <input
                  type="password"
                  className="hud-input"
                  style={styles.input}
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button type="submit" className="hud-button" style={styles.button} disabled={loading}>
              {loading ? "COMPUTING HASH..." : "INITIATE SESSION >"}
            </button>
          </form>
        )}

        {/* STEP 2: DEVICE CHECK */}
        {step === "device" && (
          <form onSubmit={handleDeviceVerify} style={styles.form}>
            <div style={styles.inputWrapper}>
              <label style={styles.label}>[HARDWARE_TOKEN_VERIFICATION]</label>
              <div style={styles.inputInner}>
                <Smartphone size={18} color="#ec4899" style={styles.inputIcon} />
                <input
                  type="text"
                  className="hud-input"
                  style={{ ...styles.input, letterSpacing: "8px", textAlign: "center" }}
                  placeholder="000000"
                  maxLength={6}
                  value={deviceCode}
                  onChange={(e) => setDeviceCode(e.target.value)}
                  required
                />
              </div>
            </div>

            <button type="submit" className="hud-button" style={styles.button} disabled={loading}>
              {loading ? "VALIDATING HARDWARE..." : "AUTHORIZE TERMINAL >"}
            </button>

            <button type="button" onClick={handleResendDeviceCode} style={styles.linkBtn}>
              <RefreshCw size={12} style={{ marginRight: "6px" }} /> Re-issue payload via Email
            </button>
          </form>
        )}

        {/* STEP 3: BIOMETRIC CHECK */}
        {step === "biometric" && (
          <div style={styles.form}>
            <div style={styles.bioBox}>
              <motion.div 
                animate={{ scale: [1, 1.1, 1] }} 
                transition={{ repeat: Infinity, duration: 2 }}
                style={styles.bioScannerCircle}
              >
                <Fingerprint size={48} color="#10b981" />
              </motion.div>
              <p style={styles.bioText}>HARDWARE PASSKEY CHALLENGE ACTIVE</p>
            </div>

            <button 
              type="button" 
              className="hud-button" 
              style={styles.button} 
              onClick={handleBiometricLogin}
              disabled={biometricStatus === "prompting"}
            >
              {biometricStatus === "prompting" ? "SCANNING SENSOR..." : "SCAN BIOMETRICS >"}
            </button>

            <button type="button" onClick={skipBiometric} style={styles.linkBtn}>
              Bypass: Use Authenticator TOTP Key
            </button>
          </div>
        )}

        {/* STEP 4: TOTP */}
        {step === "totp" && (
          <form onSubmit={handleTotpSubmit} style={styles.form}>
            <div style={styles.inputWrapper}>
              <label style={styles.label}>[DYNAMIC_TOTP_CIPHER]</label>
              <div style={styles.inputInner}>
                <KeyRound size={18} color="#8b5cf6" style={styles.inputIcon} />
                <input
                  type="text"
                  className="hud-input"
                  style={{ ...styles.input, letterSpacing: "8px", textAlign: "center" }}
                  placeholder="000000"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  required
                />
              </div>
            </div>

            <button type="submit" className="hud-button" style={styles.button} disabled={loading}>
              {loading ? "UNLOCKING ROOT..." : "GRANT ROOT ACCESS >"}
            </button>
          </form>
        )}

        {/* GAMIFIED HUD FOOTER DATA */}
        <div style={styles.hudFooter}>
          <div style={styles.hudDataBit}>
            <span style={styles.hudBitTitle}>PROTOCOL</span>
            <span style={styles.hudBitVal}>TLS_1.3 / E2EE</span>
          </div>
          <div style={styles.hudDataBit}>
            <span style={styles.hudBitTitle}>SECURITY LEVEL</span>
            <span style={{ ...styles.hudBitVal, color: "#10b981" }}>MAXIMUM</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Sub-component for gamified step tracker
function StepIndicator({ label, active, done }) {
  return (
    <div style={styles.stepIndicator}>
      <div style={{
        ...styles.stepDot,
        borderColor: active ? "#00f0ff" : done ? "#10b981" : "rgba(255,255,255,0.2)",
        backgroundColor: done ? "#10b981" : active ? "#00f0ff" : "transparent"
      }}>
        {done && <CheckCircle2 size={10} color="#000" />}
      </div>
      <span style={{
        ...styles.stepLabel,
        color: active ? "#00f0ff" : done ? "#10b981" : "rgba(255,255,255,0.4)"
      }}>
        {label}
      </span>
    </div>
  );
}

// Inline Styles for pure HUD theme
const styles = {
  viewport: {
    position: "relative",
    width: "100vw",
    height: "100vh",
    backgroundColor: "#030712",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    fontFamily: "'Courier New', Courier, monospace",
  },
  canvasContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 1,
  },
  gridOverlay: {
    position: "absolute",
    inset: 0,
    backgroundImage: "linear-gradient(to right, rgba(0, 240, 255, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(0, 240, 255, 0.05) 1px, transparent 1px)",
    backgroundSize: "40px 40px",
    animation: "gridPulse 4s infinite ease-in-out",
    pointerEvents: "none",
    zIndex: 2,
  },
  hudCard: {
    position: "relative",
    zIndex: 10,
    width: "100%",
    maxWidth: "440px",
    padding: "36px",
    background: "rgba(10, 15, 30, 0.85)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 240, 255, 0.3)",
    borderRadius: "16px",
    boxShadow: "0 0 50px rgba(0, 0, 0, 0.9), inset 0 0 15px rgba(0, 240, 255, 0.15)",
    overflow: "hidden",
  },
  scannerLine: {
    position: "absolute",
    left: 0,
    right: 0,
    height: "2px",
    background: "linear-gradient(90deg, transparent, #00f0ff, transparent)",
    animation: "scan 4s linear infinite",
  },
  header: {
    textAlign: "center",
    marginBottom: "28px",
  },
  systemStatus: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "4px",
    background: "rgba(0, 240, 255, 0.08)",
    border: "1px solid rgba(0, 240, 255, 0.2)",
    marginBottom: "12px",
  },
  systemStatusText: {
    fontSize: "10px",
    color: "#00f0ff",
    letterSpacing: "2px",
  },
  title: {
    fontSize: "22px",
    fontWeight: "900",
    color: "#ffffff",
    letterSpacing: "3px",
    margin: "0 0 20px 0",
    textShadow: "0 0 10px rgba(0, 240, 255, 0.5)",
  },
  trackerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  trackerLine: {
    flex: 1,
    height: "1px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  stepIndicator: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
  },
  stepDot: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    border: "2px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
  },
  stepLabel: {
    fontSize: "9px",
    fontWeight: "bold",
    letterSpacing: "1px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  inputWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "11px",
    color: "rgba(0, 240, 255, 0.8)",
    letterSpacing: "1px",
  },
  inputInner: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    left: "12px",
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    padding: "12px 12px 12px 40px",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box",
  },
  button: {
    width: "100%",
    padding: "14px",
    borderRadius: "8px",
    color: "#ffffff",
    fontWeight: "bold",
    fontSize: "13px",
    letterSpacing: "2px",
    cursor: "pointer",
    marginTop: "10px",
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: "rgba(0, 240, 255, 0.7)",
    fontSize: "11px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "-6px",
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "rgba(239, 68, 68, 0.15)",
    border: "1px solid rgba(239, 68, 68, 0.4)",
    padding: "10px 14px",
    borderRadius: "6px",
    marginBottom: "20px",
  },
  errorText: {
    color: "#fca5a5",
    fontSize: "11px",
    letterSpacing: "1px",
  },
  bioBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    background: "rgba(16, 185, 129, 0.05)",
    border: "1px dashed rgba(16, 185, 129, 0.3)",
    borderRadius: "12px",
  },
  bioScannerCircle: {
    padding: "16px",
    borderRadius: "50%",
    background: "rgba(16, 185, 129, 0.1)",
    border: "1px solid #10b981",
    boxShadow: "0 0 20px rgba(16, 185, 129, 0.3)",
    marginBottom: "12px",
  },
  bioText: {
    color: "#10b981",
    fontSize: "10px",
    letterSpacing: "1px",
    margin: 0,
  },
  hudFooter: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "1px solid rgba(0, 240, 255, 0.15)",
  },
  hudDataBit: {
    display: "flex",
    flexDirection: "column",
  },
  hudBitTitle: {
    fontSize: "8px",
    color: "rgba(255, 255, 255, 0.4)",
    letterSpacing: "1px",
  },
  hudBitVal: {
    fontSize: "10px",
    color: "#00f0ff",
    fontWeight: "bold",
  },
  // HUD Bracket Corner Accents
  cornerTL: { position: "absolute", top: 20, left: 20, width: 20, height: 20, borderTop: "2px solid #00f0ff", borderLeft: "2px solid #00f0ff", pointerEvents: "none" },
  cornerTR: { position: "absolute", top: 20, right: 20, width: 20, height: 20, borderTop: "2px solid #00f0ff", borderRight: "2px solid #00f0ff", pointerEvents: "none" },
  cornerBL: { position: "absolute", bottom: 20, left: 20, width: 20, height: 20, borderBottom: "2px solid #00f0ff", borderLeft: "2px solid #00f0ff", pointerEvents: "none" },
  cornerBR: { position: "absolute", bottom: 20, right: 20, width: 20, height: 20, borderBottom: "2px solid #00f0ff", borderRight: "2px solid #00f0ff", pointerEvents: "none" },
};